import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import { FormHelperText, capitalize } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import MuiAutocomplete from '@material-ui/lab/Autocomplete';

import TextField from 'Components/UI/TextField';
import { createBaseStyles } from 'Components/UI/styles';
import Chip from '@material-ui/core/Chip';
import { Avatar } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import ClearIcon from '@material-ui/icons/Clear';
import { getBorder } from 'Components/utils';
import { fade } from '@material-ui/core/styles/colorManipulator';
import ListboxComponent from './ListBoxComponent';

const styles = theme => ({
  root: {
    ...createBaseStyles(theme),
    paddingBottom: 0,
  },
  inputLabel: {},
  selectRoot: {
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  selectIcon: {},
  menuItem: {},
  helperText: {},
  groupHeader: {},
  groupedOption: {
    paddingLeft: theme.spacing(3),
  },
  chipRootAccentBlue: {
    backgroundColor: theme.palette.facet.accentBlue,
    color: theme.palette.common.white,
  },
  chipRootAccentGreen: {
    backgroundColor: theme.palette.facet.accentGreen,
    color: theme.palette.common.white,
  },
  chipRootDefault: {
    backgroundColor: theme.palette.grey[200],
  },
  chipRootSecondaryOutlined: {
    backgroundColor: theme.palette.facet.secondarySelected,
    color: theme.palette.facet.secondaryText,
    ...getBorder(theme, { all: true, color: fade(theme.palette.primary.main, 0.5) }),
  },
  chipDeleteIcon: {
    color: 'inherit',
    height: theme.spacing(2.5),
    width: theme.spacing(2.5),
  },
});

function Autocomplete({
  className,
  classes,
  error,
  helperText,
  label,
  options,
  onChange,
  selectId,
  value,
  variant,
  disabled,
  autoSelect,
  shouldSortOptions,
  shouldDisableOption,
  LabelProps,
  ignoreGroups,
  creatable,
  disableClearable,
  SelectProps: {
    multiple,
    ...restSelectProps
  } = {},
  ChipProps: {
    getRootColor,
    avatar,
  } = {},
  ...rest
}) {
  const hasGroups = !ignoreGroups && options.some(option => !!option.group);
  // For the autocomplete groups to work properly, the grouped options need to be next to each other
  if (hasGroups) {
    options.sort((a, b) => a.group.toUpperCase().localeCompare(b.group.toUpperCase()));
  } else if (shouldSortOptions) {
    options.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()));
  }

  let selectedOptionValue = null;
  if (multiple) {
    if (value != null && !Array.isArray(value)) {
      throw new Error(`Error in VirtualizedAutoComplete: value must be an array when multiple is true. Got ${JSON.stringify(value)}`);
    }
    const selectedOptionValues = [];
    if (value) {
      value.forEach((selectedValue) => {
        // consider making this a dictionary lookup
        const selectedOption = options.find(opt => opt.value === selectedValue);
        if (selectedOption) {
          selectedOptionValues.push(selectedOption);
        } else if (creatable && selectedValue) {
          // If we're creating a new value, it's not going to show up in the options list, but we still want to consider it a valid value
          selectedOptionValues.push(selectedValue);
        }
      });
    }
    if (selectedOptionValues) {
      selectedOptionValue = selectedOptionValues;
    }
  } else {
    // If we're in single select mode, find the option that corresponds with our selected value
    // consider making this a dictionary lookup
    const selectedOption = options.find(opt => opt.value === value);
    if (selectedOption) {
      selectedOptionValue = selectedOption;
    } else if (creatable) {
      selectedOptionValue = value;
    }
  }

  const renderGroup = params => [
    <MenuItem
      className={classes.groupHeader}
      color="primary"
      disabled
      key={`header.${params.group}`} // eslint-disable-line react/no-array-index-key
    >
      {params.group}
    </MenuItem>,
    params.children,
  ];

  // renderOption is used to render each option when the dropdown is open
  const renderOption = option => (
    <MenuItem
      className={classNames(classes.menuItem, {
        [classes.groupedOption]: !!option.group,
      })}
      key={option.value}
      value={option.value}
    >
      {option.name}
    </MenuItem>
  );

  // renderInput is used to render the input field that the user types into
  const renderInput = (params) => {
    return (
      <TextField {...params} variant={variant} label={label && label} />
    );
  };

  const defaultValue = multiple ? [] : null;

  // Determines Chip root color class
  const chipRootColor = (option) => {
    if (getRootColor) {
      return getRootColor(option);
    }
    return 'default';
  };

  return (
    <FormControl
      error={error}
      className={classNames(classes.root, className)}
      variant={variant}
      {...rest}
    >
      <MuiAutocomplete
        id={selectId}
        autoComplete
        disableClearable={disableClearable}
        autoSelect={autoSelect}
        freeSolo={creatable}
        autoHighlight
        disabled={disabled}
        value={selectedOptionValue || defaultValue}
        onChange={(evt, newValue) => {
          // We need to map the changed autocomplete value to a form that's compatible with the users of the SelectControl
          let transformedValue;
          if (multiple && Array.isArray(newValue)) {
            // If we are in creatable mode, the created value won't be an object with a value attribute
            // However, we can still select existing values from the list. These will have value attributes
            // If v and v.value exist, use v.value. Otherwise, just use v
            transformedValue = newValue ? newValue.map(v => v?.value || v) : [];
          } else {
            transformedValue = newValue ? newValue.value : null;
          }
          onChange({ target: { value: transformedValue } });
        }}
        fullWidth
        options={options}
        getOptionLabel={option => (option?.name || '')}
        getOptionDisabled={option => shouldDisableOption(option.value)}
        groupBy={hasGroups ? option => option.group : undefined}
        ListboxComponent={ListboxComponent}
        renderGroup={(hasGroups) ? renderGroup : undefined}
        renderInput={renderInput}
        renderOption={renderOption}
        renderTags={(v, getTagProps) => v.map((option, index) => (
          <Chip
            avatar={avatar && <Avatar alt={option.name} src={option.media} />}
            label={option.name || option}
            classes={{
              root: classes[`chipRoot${capitalize(chipRootColor(option))}`],
            }}
            deleteIcon={<ClearIcon className={classes.chipDeleteIcon} />}
            {...getTagProps({ index })}
          />
        ))
        }
        multiple={multiple}
        {...restSelectProps}
      />
      {helperText && (
        <FormHelperText className={classes.helperText}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

Autocomplete.defaultProps = {
  className: '',
  label: null,
  variant: 'standard',
  shouldDisableOption: () => false,
  shouldSortOptions: false,
  ignoreGroups: false,
  creatable: false,
  autoSelect: true,
  disableClearable: false,
};

Autocomplete.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  selectId: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]).isRequired,
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  label: PropTypes.string,
  shouldDisableOption: PropTypes.func,
  shouldSortOptions: PropTypes.bool,
  ignoreGroups: PropTypes.bool,
  creatable: PropTypes.bool,
  autoSelect: PropTypes.bool,
  disableClearable: PropTypes.bool,
};

export default withStyles(styles, { name: 'Autocomplete' })(Autocomplete);
